import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link, useParams } from "react-router-dom";
import { CalendarIcon, ClockIcon, UserIcon, TrendingUp, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_id: string;
  status: string;
  published_at: string;
  featured_image_url: string;
  category: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  staff_members?: {
    first_name: string;
    last_name: string;
  } | null;
  profiles?: {
    staff_members: {
      first_name: string;
      last_name: string;
    };
  } | null;
}

const Blog = () => {
  // The Categories sidebar has always linked to /blog/category/<slug>. That route did not exist, so
  // every one of those buttons 404'd on the public site. The route now maps here and this param
  // drives the filter. Slugging must match the link exactly: lowercase, whitespace -> "-".
  const { category: categoryParam } = useParams<{ category?: string }>();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      console.log('Fetching blog posts...');
      try {
        // First get the blog posts (only public blog posts)
        const { data: posts, error: postsError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .contains('blog_types', ['public'])
          .order('published_at', { ascending: false });

        console.log('Blog posts query result:', { posts, postsError });

        if (postsError) {
          console.error('Error fetching blog posts:', postsError);
          // Still try to show the page even if there's an error
          setLoading(false);
          return;
        }

        if (posts && posts.length > 0) {
          // Get unique author IDs
          const authorIds = [...new Set(posts.map(post => post.author_id))];
          console.log('Author IDs to fetch:', authorIds);
          
          // Fetch author information using the public function
          const authorPromises = authorIds.map(async (authorId) => {
            console.log('Fetching author for ID:', authorId);
            try {
              const { data: authorData, error } = await supabase.rpc('get_blog_author', {
                author_user_id: authorId
              });
              
              console.log('Author data result:', { authorData, error });
              
              if (error) {
                console.error('Error fetching author:', error);
                return { userId: authorId, staffMember: null };
              }
              
              if (authorData && authorData.length > 0) {
                return {
                  userId: authorId,
                  staffMember: authorData[0]
                };
              }
              
              return { userId: authorId, staffMember: null };
            } catch (err) {
              console.error('Exception fetching author:', err);
              return { userId: authorId, staffMember: null };
            }
          });

          try {
            const authorResults = await Promise.all(authorPromises);
            console.log('Author results:', authorResults);

            // Create author lookup map
            const authorMap: Record<string, any> = {};
            authorResults.forEach(result => {
              if (result.staffMember) {
                authorMap[result.userId] = result.staffMember;
              }
            });

            console.log('Author map created:', authorMap);

            // Attach author info to posts
            const postsWithAuthors = posts.map(post => ({
              ...post,
              staff_members: authorMap[post.author_id] || null
            }));

            // Extract unique categories from posts
            const uniqueCategories = [...new Set(posts.map(p => p.category).filter(Boolean))] as string[];
            setCategories(uniqueCategories);

            console.log('Setting blog posts with authors:', postsWithAuthors);
            setFeaturedPost(postsWithAuthors[0]);
            setBlogPosts(postsWithAuthors.slice(1));
          } catch (authorError) {
            console.error('Error processing authors, showing posts without author info:', authorError);
            // Extract categories even if author fetching fails
            const uniqueCategories = [...new Set(posts.map(p => p.category).filter(Boolean))] as string[];
            setCategories(uniqueCategories);
            // Still show posts even if author fetching fails
            setFeaturedPost(posts[0]);
            setBlogPosts(posts.slice(1));
          }
        } else {
          console.log('No blog posts found');
        }
      } catch (error) {
        console.error('Error:', error);
        // Don't let errors prevent the page from loading
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, '');
    const wordCount = textContent.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getAuthorName = (post: BlogPost) => {
    if (post.staff_members) {
      return `${post.staff_members.first_name} ${post.staff_members.last_name}`;
    }
    if (post.profiles?.staff_members) {
      return `${post.profiles.staff_members.first_name} ${post.profiles.staff_members.last_name}`;
    }
    console.log('No author found for post:', post.title, 'Author ID:', post.author_id);
    return 'Unknown Author';
  };


  if (loading) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading blog posts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Category filtering ────────────────────────────────────────────────────────────────────────
  // Must slug identically to the sidebar link so /blog/category/<slug> round-trips.
  const slugifyCategory = (c: string) => c.toLowerCase().replace(/\s+/g, "-");
  // The featured post is held separately from the list, so it has to be folded back in before
  // filtering — otherwise a category whose only post happens to be the featured one shows empty.
  const allPosts = featuredPost ? [featuredPost, ...blogPosts] : blogPosts;
  const filteredPosts = categoryParam
    ? allPosts.filter((p) => p.category && slugifyCategory(p.category) === categoryParam)
    : blogPosts;
  // Resolve the slug back to its display name for the heading; fall back to the raw slug.
  const activeCategoryLabel = categoryParam
    ? categories.find((c) => slugifyCategory(c) === categoryParam) ?? categoryParam
    : null;

  // Show empty state if no posts are available
  if (!featuredPost && blogPosts.length === 0) {
    return (
      <div className="py-8">
        {/* Header */}
        <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Investment Insights</h1>
              <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
                Stay informed with our latest market analysis, investment strategies, and financial planning insights 
                from our team of investment professionals.
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📝</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">No Blog Posts Available</h2>
            <p className="text-muted-foreground mb-6">
              We're working on bringing you the latest investment insights. Check back soon for new articles!
            </p>
            <Button asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <SEO
        title="Insights & Market Commentary — CBAM Blog"
        description="Investment insights, market commentary and portfolio updates from Cape Berkshire Asset Management's investment team for UK financial advisers."
        path="/blog"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Investment Insights</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              Stay informed with our latest market analysis, investment strategies, and financial planning insights 
              from our team of investment professionals.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        {/* Year End Review Featured Banner */}
        <div className="mb-12">
          <Link to="/blog/year-end-review-2025" className="block group">
            <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white">
              <div className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                    <Badge className="bg-white/20 text-white border-0">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Special Report
                    </Badge>
                    <span className="text-sm text-white/70">December 2025</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">End of Year Investment Review 2025</h2>
                  <p className="text-white/80 max-w-2xl">
                    Exceptional returns across all portfolios. Explore our comprehensive performance analysis with interactive charts and detailed fund breakdowns.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Button variant="secondary" size="lg" className="group-hover:scale-105 transition-transform">
                    View Report
                  </Button>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Featured Post */}
            {/* The featured hero is suppressed while filtering by category: it is folded into
                filteredPosts above, so showing it here too would duplicate it and would also
                surface an off-category post above the filtered list. */}
            {featuredPost && !categoryParam && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6">Featured Article</h2>
                <Card className="overflow-hidden">
                  <div className="aspect-video relative">
                    {featuredPost.featured_image_url ? (
                      <img 
                        src={featuredPost.featured_image_url} 
                        alt={featuredPost.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="bg-gradient-to-br from-primary/10 to-accent/10 w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl">📊</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <Badge>{featuredPost.category}</Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl">{featuredPost.title}</CardTitle>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <UserIcon className="h-4 w-4" />
                        {getAuthorName(featuredPost)}
                      </div>
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(featuredPost.published_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {estimateReadTime(featuredPost.content)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base mb-6">
                      {featuredPost.excerpt}
                    </CardDescription>
                    <Button asChild>
                      <Link to={`/blog/${featuredPost.slug}`}>Read Full Article</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Recent Posts — or the filtered set when viewing /blog/category/<slug> */}
            <div>
              <div className="flex items-baseline justify-between gap-4 mb-6 flex-wrap">
                <h2 className="text-2xl font-bold">
                  {activeCategoryLabel ? `Articles in “${activeCategoryLabel}”` : "Recent Articles"}
                </h2>
                {activeCategoryLabel && (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/blog">Clear filter</Link>
                  </Button>
                )}
              </div>
              {activeCategoryLabel && filteredPosts.length === 0 && (
                <p className="text-muted-foreground mb-6">
                  No articles in this category yet.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPosts.map((post) => (
                  <Card key={post.id} className="overflow-hidden">
                    <div className="aspect-video relative">
                      {post.featured_image_url ? (
                        <img 
                          src={post.featured_image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="bg-gradient-to-br from-muted to-muted/50 w-full h-full flex items-center justify-center">
                          <span className="text-2xl">📄</span>
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <Badge>{post.category}</Badge>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <UserIcon className="h-3 w-3" />
                          {getAuthorName(post)}
                        </div>
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {formatDate(post.published_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-3 w-3" />
                          {estimateReadTime(post.content)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </CardDescription>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/blog/${post.slug}`}>Read More</Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-8">
              {/* Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {categories.map((category, index) => (
                      <Button 
                        key={index} 
                        variant="ghost" 
                        className="w-full justify-start text-sm h-auto p-2"
                        asChild
                      >
                        <Link to={`/blog/category/${category.toLowerCase().replace(/\s+/g, '-')}`}>
                          {category}
                        </Link>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter Signup */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Stay Informed</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm mb-4">
                    Subscribe to our newsletter for weekly market insights and investment updates.
                  </CardDescription>
                  <Button className="w-full" asChild>
                    <Link to="/contact">Subscribe Now</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Popular Posts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Popular This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {blogPosts.slice(0, 3).map((post) => (
                      <div key={post.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                        <h4 className="font-medium text-sm line-clamp-2 mb-2">
                          <Link to={`/blog/${post.slug}`} className="hover:text-primary">
                            {post.title}
                          </Link>
                        </h4>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(post.published_at)} • {estimateReadTime(post.content)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Professional Investment Advice?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Our investment insights are just the beginning. Get personalised advice 
            tailored to your specific financial situation.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">Book Your Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Blog;